import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import QuestionLibraryManager from "@components/admin-agent-questions/QuestionLibraryManager";
import DefaultFlowManager from "@components/admin-agent-questions/DefaultFlowManager";
import { useAdminAgentQuestions } from "@hooks/useAdminAgentQuestions";

export default function AdminAgentQuestions() {
  const navigate = useNavigate();

  const {
    PURPOSES,
    loading,
    saving,

    tab,
    setTab,

    query,
    setQuery,
    filteredLibrary,
    library,
    libraryByEnum,

    createQuestion,
    updateQuestion,
    deleteQuestion,

    purpose,
    setPurpose,

    defaultFlow,
    saveFlow,

    error,
    clearError,
  } = useAdminAgentQuestions();

  // Local draft for DEFAULT flow editing
  const [draftFlow, setDraftFlow] = useState([]);

  useEffect(() => {
    setDraftFlow(defaultFlow || []);
  }, [defaultFlow]);

  const isDirty = useMemo(() => {
    return JSON.stringify(draftFlow || []) !== JSON.stringify(defaultFlow || []);
  }, [draftFlow, defaultFlow]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button color="light" onClick={() => navigate(-1)}>
            ← Back
          </Button>

          <div>
            <h1 className="text-lg sm:text-xl font-semibold">
              Admin: Agent Questions
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Manage the question library, rules, and default flows for each purpose.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            color={tab === "LIBRARY" ? "dark" : "light"}
            onClick={() => {
              clearError();
              setTab("LIBRARY");
            }}
          >
            Question Library
          </Button>
          <Button
            color={tab === "DEFAULTS" ? "dark" : "light"}
            onClick={() => {
              clearError();
              setTab("DEFAULTS");
            }}
          >
            Default Flows
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Spinner size="sm" />
          <span>Loading…</span>
        </div>
      )}

      {tab === "LIBRARY" ? (
        <QuestionLibraryManager
          query={query}
          onQueryChange={setQuery}
          questions={filteredLibrary}
          onCreate={createQuestion}
          onUpdate={updateQuestion}
          onDelete={deleteQuestion}
          error={error}
          onClearError={clearError}
        />
      ) : PURPOSES?.length ? (
        <DefaultFlowManager
          purposes={PURPOSES}
          purpose={purpose}
          setPurpose={(p) => {
            if (isDirty) {
              const ok = window.confirm(
                "You have unsaved changes. Switch purpose and discard them?"
              );
              if (!ok) return;
            }
            clearError();
            setPurpose(p);
          }}
          draftFlow={draftFlow}
          setDraftFlow={setDraftFlow}
          onSave={() => saveFlow(draftFlow)}
          saving={saving}
          canSave={isDirty && !saving}
          library={library}
          libraryByEnum={libraryByEnum}
          error={error?.scope === "defaults" ? error : null}
          onClearError={clearError}
        />
      ) : (
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-sm font-medium">Loading purposes…</div>
          <div className="text-xs text-gray-500 mt-1">
            Purposes are fetched from the backend.
          </div>
        </div>
      )}
    </div>
  );
}
